import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Store } from '@ngxs/store';
import {
  RemoveRole, SetAllRoles, SetRole, SetRoleDataLoaded,
  SetSelectedRole, SetAssignedSites, SetAllSites, SetAssignedSiteCount,
  SetSelectedPermissionSite, SetAssignedUsers, SetAllUsers,
  SetAssignedZones, SetAllZones, ClearRoleSession
} from 'src/app/Store/role.state/roles.actions';
import { RolesState } from 'src/app/Store/role.state/roles.state';
import { IRole } from 'src/app/Store/role.state/roles.model';
import { IRoleSite } from 'src/app/Store/sites.state/sites.model';
import { IRoleUser } from 'src/app/Store/user.state/users.model';
import { tap, map, catchError } from 'rxjs/operators';
import { ServerApi } from 'src/app/services/api.services/server.api';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { MessagesBank } from 'src/app/services/status.service';
export interface IZonePermission {
  id: string;
  name: string;
  description?: string;
  hasPermission: boolean;
  homeZone: boolean;
}

export interface RoleAction {
  action: 'add' | 'edit' | 'delete' | 'refresh';
  role?: IRole | null;
  context?: any;
}

@Injectable({ providedIn: 'root' })
export class RolesService implements OnDestroy {

  private readonly actionSubject = new Subject<RoleAction>();
  public readonly action$: Observable<RoleAction> = this.actionSubject.asObservable();

  private readonly rolesSubscription: Subscription;

  constructor(private readonly store: Store, private readonly serverApi: ServerApi) {
    this.rolesSubscription = this.store.select(RolesState.getRoles).subscribe((roles: IRole[]) => {
      this.syncSelectedRoleWithStore(roles);
    });
  }

  private syncSelectedRoleWithStore(storeRoles: IRole[]): void {
    const current = this.store.selectSnapshot(RolesState.getSelectedRole);
    if (current && storeRoles.length > 0) {
      const updated = storeRoles.find(r => r.id === current.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(current)) {
        this.store.dispatch(new SetSelectedRole(updated));
      }
    }
  }

  // ── Selected role ─────────────────────────────────────────────────────────

  setSelectedRole(role: IRole | null): void {
    this.store.dispatch(new SetSelectedRole(role));
    if (!role) {
      this.store.dispatch(new ClearRoleSession());
    }
  }

  getSelectedRole(): IRole | null {
    return this.store.selectSnapshot(RolesState.getSelectedRole);
  }

  triggerAction(action: RoleAction): void {
    this.actionSubject.next(action);
  }

  // ── Sites ─────────────────────────────────────────────────────────────────

  setAssignedSiteCount(count: number): void {
    this.store.dispatch(new SetAssignedSiteCount(count));
  }

  setAssignedSites(sites: IRoleSite[]): void {
    this.store.dispatch(new SetAssignedSites(sites));
  }

  getAssignedSites(): IRoleSite[] {
    return this.store.selectSnapshot(RolesState.getAssignedSites);
  }

  setAllSites(sites: IRoleSite[]): void {
    this.store.dispatch(new SetAllSites(sites));
  }

  getAllSites(): IRoleSite[] {
    return this.store.selectSnapshot(RolesState.getAllSites);
  }

  updateRoleAssignSitesCount(roleId: string | number, count: number): void {
    const normalizedCount = Math.max(0, count);
    const roles = this.store.selectSnapshot(RolesState.getRoles);
    const selectedRole = this.store.selectSnapshot(RolesState.getSelectedRole);
    const roleToUpdate = roles.find(role => role.id === roleId) || (selectedRole?.id === roleId ? selectedRole : null);

    if (roleToUpdate) {
      const updatedRole: IRole = {
        ...roleToUpdate,
        assignSitesCount: normalizedCount
      };

      this.store.dispatch(new SetRole(updatedRole));

      if (selectedRole?.id === roleId) {
        this.store.dispatch(new SetSelectedRole(updatedRole));
      }
    }
  }

  // ── Permission site ───────────────────────────────────────────────────────

  setSelectedPermissionSite(site: IRoleSite | null): void {
    this.store.dispatch(new SetSelectedPermissionSite(site));
  }

  getSelectedPermissionSite(): IRoleSite | null {
    return this.store.selectSnapshot(RolesState.getSelectedPermissionSite);
  }

  // ── Users ─────────────────────────────────────────────────────────────────

  setAssignedUsers(users: IRoleUser[]): void {
    this.store.dispatch(new SetAssignedUsers(users));
  }

  getAssignedUsers(): IRoleUser[] {
    return this.store.selectSnapshot(RolesState.getAssignedUsers);
  }

  setAllUsers(users: IRoleUser[]): void {
    this.store.dispatch(new SetAllUsers(users));
  }

  getAllUsers(): IRoleUser[] {
    return this.store.selectSnapshot(RolesState.getAllUsers);
  }

  // ── Zones ─────────────────────────────────────────────────────────────────

  setAssignedZones(zones: IZonePermission[]): void {
    this.store.dispatch(new SetAssignedZones(zones));
  }

  setAllZones(zones: IZonePermission[]): void {
    this.store.dispatch(new SetAllZones(zones));
  }

  getAllZones(): IZonePermission[] {
    return this.store.selectSnapshot(RolesState.getAllZones);
  }

  loadRoles(): void {
    this.store.dispatch(new SetRoleDataLoaded(false)); // Set loading to false (data not loaded)
    this.serverApi.statusBar.addNewStatus(MessagesBank.LOADING_USERS); // Using existing message
    this.serverApi.sendGetToServer(`${environment.serverUrl}/newapi/roles`)
      .subscribe({
        next: (roles: any[]) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.LOADING_USERS);
          roles.forEach(role => {
            role.id = role['id'].value;
          });
          this.store.dispatch(new SetAllRoles(roles));
          this.store.dispatch(new SetRoleDataLoaded(true)); // Data loaded successfully
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.LOADING_USERS);
          this.store.dispatch(new SetRoleDataLoaded(true)); // Set to true even on error to stop loading
          const errorMessage = 'Error in loading roles. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Loading Roles');
        }
      });
  }

  getSites(roleId: string | number): Observable<IRoleSite[]> {
    return this.serverApi.sendGetToServer(`${environment.serverUrl}/newapi/sites?roleId=${roleId}`);
  }

  assignSitesToRole(roleId: string | number, siteIds: number[], onSuccess?: (siteIds: number[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.ASSIGNING_SITES);
    const payload = { siteIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/sites/assign`, payload)
      .subscribe({
        next: () => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_SITES);
          if (onSuccess) { onSuccess(siteIds); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_SITES);
          const errorMessage = 'Error in assigning sites. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Assigning Sites');
        }
      });
  }

  unassignSitesFromRole(roleId: string | number, siteIds: number[], onSuccess?: (siteIds: number[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UNASSIGNING_SITES);
    const payload = { siteIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/sites/unAssign`, payload)
      .subscribe({
        next: () => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_SITES);
          if (onSuccess) { onSuccess(siteIds); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_SITES);
          const errorMessage = 'Error in unassigning sites. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Unassigning Sites');
        }
      });
  }

  // User management methods
  getUsers(roleName: string): Observable<IRoleUser[]> {
    return this.serverApi.sendGetToServer(`${environment.serverUrl}/newapi/users?roleName=${roleName}`)
      .pipe(map((users: IRoleUser[]) => users.filter(user => user.candidate === false)));
  }

  assignUsersToRole(roleId: string | number, userIds: string[], onSuccess?: (userIds: string[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.ASSIGNING_USERS);
    const payload = { userIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/users/assign`, payload)
      .subscribe({
        next: (res: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_USERS);
          if (onSuccess) { onSuccess(res.data?.userIds || []); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_USERS);
          const errorMessage = 'Error in assigning users. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Assigning Users');
        }
      });
  }

  unassignUsersFromRole(roleId: string | number, userIds: string[], onSuccess?: (userIds: string[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UNASSIGNING_USERS);
    const payload = { userIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/users/unAssign`, payload)
      .subscribe({
        next: (res: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_USERS);
          if (onSuccess) { onSuccess(res.data?.userIds || []); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_USERS);
          const errorMessage = 'Error in unassigning users. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Unassigning Users');
        }
      });
  }

  // Role management methods
  createRole(name: string, description: string): void {
    const roleData = { name, description };
    this.serverApi.statusBar.addNewStatus(MessagesBank.CREATEING_ROLE);
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles`, roleData)
      .subscribe({
        next: (newRole: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.CREATEING_ROLE);
          const role: IRole = {...newRole, id: newRole['id'].value, assignSitesCount: 0};
          this.store.dispatch(new SetRole(role));
          
          // Auto-select the newly created role
          this.setSelectedRole(role);
          
          // Trigger action for UI to handle auto-scrolling
          this.triggerAction({ action: 'add', role: role });
        },
        error: (res) => {
          if (res.error?.error === 'DuplicateRoleName') {
            const errorMessage = `A role with the name "${roleData.name}" already exists. Please choose a different name.`;
            this.serverApi.createNotifiactionDialogForHttpCrisis(res, errorMessage, false, 'Duplicate Role Name');
          } else {
            const errorMessage = 'Error in creating role. Please contact your system administrator.';
            this.serverApi.createNotifiactionDialogForHttpCrisis(res, errorMessage);
          }
          this.serverApi.statusBar.removeStatus(MessagesBank.CREATEING_ROLE);
        }
      });
  }

  deleteRole(roleId: number): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.DELETING_ROLE);
    
    this.serverApi.sendDeleteToServer(`${environment.serverUrl}/newapi/delete/roles/${roleId}`)
      .subscribe({
        next: () => {
          this.serverApi.statusBar.removeStatus(MessagesBank.DELETING_ROLE);
          
          // If the deleted role was selected, clear the selection
          const selectedRole = this.getSelectedRole();
          if (selectedRole?.id == roleId) {
            this.setSelectedRole(null);
          }
          
          this.store.dispatch(new RemoveRole(roleId));
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.DELETING_ROLE);
          const errorMessage = 'Error in deleting role. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Deleting Role');
        }
      });
  }

  copyRoleSettings(sourceRoleId: string | number, targetRoleId: string | number): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UPDATING_ROLE);
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${sourceRoleId}/copy-settings/${targetRoleId}`, {})
      .subscribe({
        next: () => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
          this.actionSubject.next({ action: 'refresh' });
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
          const errorMessage = 'Error in copying role settings. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Copying Role Settings');
        }
      });
  }

  updateRoleSchedule(rolePayload: IRole & {
    companyId?: number | null;
    createdByApp?: string | null;
    ldapDN?: string | null;
    permissions?: unknown;
    validityDates?: Array<{ startDate: string | Date; endDate: string | Date }>;
  }): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UPDATING_ROLE);

    const payload = {
      ...rolePayload,
      hasDateValidity: rolePayload.hasDateValidity,
      validityDates: (rolePayload.validityDates || [])
        .filter((dateRange) => !!dateRange?.startDate && !!dateRange?.endDate)
        .map((dateRange) => ({
          startDate: this.toApiDateString(dateRange.startDate),
          endDate: this.toApiDateString(dateRange.endDate)
        }))
    };

    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/role/validitydates`, payload)
      .subscribe({
        next: (response: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
          const roles = this.store.selectSnapshot(RolesState.getRoles);
          const existingRole = roles.find(r => r.id === rolePayload.id);
          const updatedRole: IRole = {
            ...response,
            id: response['id'].value,
            assignSitesCount: existingRole?.assignSitesCount ?? rolePayload.assignSitesCount ?? 0
          };

          this.store.dispatch(new SetRole(updatedRole));
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
          const errorMessage = 'Error in updating role schedule. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Updating Role Schedule');
        }
      });
  }

  private toApiDateString(value: string | Date): string {
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return value;
  }

  updateRole(role: { id: string | number, name: string, description: string }): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UPDATING_ROLE);
    
    this.serverApi.sendPutToServer(`${environment.serverUrl}/newapi/roles/${role.id}`, {
      name: role.name,
      description: role.description
    }).subscribe({
      next: (updatedRole: any) => {
        this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
        const existingRoles = this.store.selectSnapshot(RolesState.getRoles);
        const existing = existingRoles.find(r => r.id === updatedRole['id']?.value);
        const role: IRole = {
          ...updatedRole,
          id: updatedRole['id'].value,
          assignSitesCount: existing?.assignSitesCount ?? 0
        };
        this.store.dispatch(new SetRole(role));
      },
      error: (res) => {
        if (res.error?.error === 'DuplicateRoleName') {
          const errorMessage = `A role with the name "${role.name}" already exists. Please choose a different name.`;
          this.serverApi.createNotifiactionDialogForHttpCrisis(res, errorMessage, false, 'Duplicate Role Name');
        } else {
          const errorMessage = 'Error in updating role. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(res, errorMessage);
        }
        this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_ROLE);
      }
    });
  }

  // Zone permission methods
  getZones(siteId: string | number, roleId: string | number): Observable<IZonePermission[]> {
    return this.serverApi.sendGetToServer(`${environment.serverUrl}/newapi/zones/manage?siteId=${siteId}&roleId=${roleId}`);
  }

  assignZonesToRole(siteId: string | number, roleId: string | number, zoneIds: string[], onSuccess?: (zoneIds: string[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.ASSIGNING_ZONES);
    const payload = { zoneIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/sites/${siteId}/zones/assign`, payload)
      .subscribe({
        next: (res: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_ZONES);
          if (onSuccess) { onSuccess(res.data?.zoneIds || []); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.ASSIGNING_ZONES);
          const errorMessage = 'Error in assigning zones. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Assigning Zones');
        }
      });
  }

  unassignZonesFromRole(siteId: string | number, roleId: string | number, zoneIds: string[], onSuccess?: (zoneIds: string[]) => void): void {
    this.serverApi.statusBar.addNewStatus(MessagesBank.UNASSIGNING_ZONES);
    const payload = { zoneIds };
    this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/sites/${siteId}/zones/unAssign`, payload)
      .subscribe({
        next: (res: any) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_ZONES);
          if (onSuccess) { onSuccess(res.data?.zoneIds || []); }
        },
        error: (error) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UNASSIGNING_ZONES);
          const errorMessage = 'Error in unassigning zones. Please contact your system administrator.';
          this.serverApi.createNotifiactionDialogForHttpCrisis(error, errorMessage, false, 'Unassigning Zones');
        }
      });
  }

  // Layer permission methods
  getLayersPermissions(siteId: string | number, roleId: string | number): Observable<any[]> {
    console.log(`Calling layers API: siteId=${siteId}, roleId=${roleId}`);
    
    return this.serverApi.sendGetToServer(`${environment.serverUrl}/newapi/layers/manage?siteId=${siteId}&roleId=${roleId}`)
      .pipe(
        tap((response: any) => console.log('Layers API response received:', response)),
        map((response: any) => response || []),
        catchError((error) => {
          console.error('Error in loading layers permissions:', error);
          return throwError(() => error);
        })
      );
  }

  getDefaultLayerPermissions(siteId: string | number, roleId?: string | number): Observable<any> {
    console.log(`Calling default layers API: siteId=${siteId}, roleId=${roleId}`);
    
    let url = `${environment.serverUrl}/newapi/layers/defaultPermissions?siteId=${siteId}`;
    if (roleId && roleId !== "undefined") {
      url += `&roleId=${roleId}`;
    }
    
    return this.serverApi.sendGetToServer(url)
      .pipe(
        tap((response: any) => console.log('Default layers API response received:', response)),
        map((response: any) => response || null),
        catchError((error) => {
          console.error('Error in loading default layer permissions:', error);
          return throwError(() => error);
        })
      );
  }

  getGeneralLayerPermissions(siteId: string | number, roleId?: string | number): Observable<any[]> {
    console.log(`Calling general layers API: siteId=${siteId}, roleId=${roleId}`);
    
    let url = `${environment.serverUrl}/newapi/layers/general?siteId=${siteId}`;
    if (roleId && roleId !== "undefined") {
      url += `&roleId=${roleId}`;
    }
    
    return this.serverApi.sendGetToServer(url)
      .pipe(
        tap((response: any) => console.log('General layers API response received:', response)),
        map((response: any) => response || []),
        catchError((error) => {
          console.error('Error in loading general layer permissions:', error);
          return throwError(() => error);
        })
      );
  }

  saveLayersPermissions(roleId: string | number, siteId: string | number, payload: any): Observable<any> {
    console.log(`Saving layers permissions: roleId=${roleId}, siteId=${siteId}`, payload);
    
    return this.serverApi.sendPostToServer(`${environment.serverUrl}/newapi/roles/${roleId}/sites/${siteId}/layers`, payload)
      .pipe(
        tap((response: any) => console.log('Save layers permissions response:', response)),
        catchError((error) => {
          console.error('Error saving layers permissions:', error);
          return throwError(() => error);
        })
      );
  }

  ngOnDestroy(): void {
    if (this.rolesSubscription) {
      this.rolesSubscription.unsubscribe();
    }
  }
}